import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const MAX = 6;

export default function PaymentSuccess() {
  const { setUser } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan_id);
          if (data.user) setUser(data.user);
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("error"); return; }
        attempts += 1;
        if (attempts >= MAX) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX) { setState("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [setUser]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-[#0E1118] p-8 text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-blue-500" />
            <h2 className="font-heading text-xl font-bold text-white">{t("pay.checking")}</h2>
            <p className="mt-2 text-sm text-slate-500">{t("pay.checkingSub")}</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-400" />
            <h2 className="font-heading text-2xl font-bold text-white">{t("pay.success")}</h2>
            <p className="mt-2 text-sm text-slate-400">{t("pay.yourPlan")} <span className="font-semibold text-white">{plan ? t(`plan.${plan}`) : ""}</span> {t("pay.activeFor")}</p>
            <Link to="/dashboard" data-testid="go-dashboard" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
              {t("pay.goTerminal")} <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-amber-500" />
            <h2 className="font-heading text-xl font-bold text-white">{t("pay.pending")}</h2>
            <p className="mt-2 text-sm text-slate-500">{t("pay.pendingSub")}</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
            <h2 className="font-heading text-xl font-bold text-white">{t("pay.errTitle")}</h2>
            <p className="mt-2 text-sm text-slate-500">{t("pay.errSub")}</p>
            <Link to="/tarifs" className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800">
              {t("pay.backPricing")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
